/**
 * CommandPalette — Cmd+K global search / navigation overlay.
 *
 * Spec: Docs/design/03-components.md §7
 *
 * Lists navigation targets, quick actions, and (PROD-2) live tools from
 * the Action Registry (`GET /tools/manifest`), and filters them as the
 * user types. Keyboard: arrows to move, Enter to execute, Escape to
 * close. Parameterless tools invoke directly from here; tools that
 * require arguments deep-link to `ToolDetailPage` instead of guessing.
 *
 * PROD-2c — HITL: a tool guarded by a `ConfirmationPolicy` answers
 * `POST /tools/{id}:invoke` with 409 `confirmation_required` instead of
 * running. The palette renders that prompt inline and, on approve,
 * re-invokes with the 409's one-time `confirmation_token` in
 * `x-omni-confirmation` — consent for exactly that call (RW-1.19).
 *
 * Not built here (see UI-1): the CMDK migration Docs/design/03-components.md
 * §7 mandates, blocked on a repo-wide shadcn/Radix adoption decision.
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { getToolsManifest, invokeTool } from "@/api";
import type { ConfirmationRequiredDetail, ToolManifestItem } from "@/api";
import { useApiClient } from "@/api/useApiClient";
import { useAuth } from "@/auth/useAuth";
import { useTheme } from "@/app/theme";
import { cn } from "@/lib/cn";
import { apiErrorMessage } from "@/lib/api-error";

interface CmdItem {
  id: string;
  label: string;
  hint?: string;
  shortcut?: string;
  category: "navigate" | "action" | "tool";
  action: () => void | Promise<void>;
}

function hasRequiredParams(tool: ToolManifestItem): boolean {
  const required = (tool.parameters as { required?: unknown } | undefined)?.required;
  return Array.isArray(required) && required.length > 0;
}

/**
 * Reasons the palette can resolve by itself.
 *
 * `POST /tools/{id}:invoke`'s resume path is a **binary proceed** — it
 * reads only the `x-omni-confirmation` token and re-runs with the
 * original arguments. There is nowhere to send collected input, so
 * this surface genuinely cannot satisfy a reason that asks the user
 * *for* something. Offering an Approve
 * button for those would run the tool without the input it asked for —
 * so they deep-link to the tool page instead.
 */
const BINARY_REASONS: ReadonlySet<string> = new Set([
  "approval_required",
  "credit_notice",
]);

export function isBinaryConfirmation(detail: ConfirmationRequiredDetail): boolean {
  return BINARY_REASONS.has(detail.reason);
}

/** Live tools from the Action Registry — PROD-2. Fetched lazily each
 * time the palette opens rather than kept resident; the manifest is
 * small and this keeps the palette free of a global-state dependency. */
function useToolItems(
  isOpen: boolean,
  navigate: ReturnType<typeof useNavigate>,
  onClose: () => void,
  runTool: (toolId: string) => Promise<void>,
): CmdItem[] {
  const client = useApiClient();
  const [tools, setTools] = React.useState<ToolManifestItem[]>([]);

  React.useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    getToolsManifest({ client, query: { role: "frontier" } })
      .then(({ data }) => {
        if (!cancelled && data) setTools(data.tools);
      })
      .catch(() => {
        // Best-effort — nav/action items still work if this fails.
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, client]);

  return React.useMemo<CmdItem[]>(
    () =>
      tools.map((t) => ({
        id: `tool-${t.id}`,
        label: t.title,
        hint: t.description,
        category: "tool",
        action: () => {
          // Only argument-taking tools deep-link — the palette can't
          // invent arguments. Confirmation-gated tools now invoke and
          // resolve their 409 inline (PROD-2c); before that they were
          // bounced here too, which made the 409 path unreachable.
          if (hasRequiredParams(t)) {
            navigate(`/tools/${encodeURIComponent(t.id)}`);
            onClose();
            return;
          }
          return runTool(t.id);
        },
      })),
    [tools, navigate, onClose, runTool],
  );
}

function usePaletteItems(onClose: () => void): CmdItem[] {
  const navigate = useNavigate();
  const auth = useAuth();
  const { cycle } = useTheme();

  return React.useMemo<CmdItem[]>(() => {
    const nav = (label: string, href: string, hint?: string): CmdItem => ({
      id: `nav-${href}`,
      label,
      hint,
      category: "navigate",
      action: () => { navigate(href); onClose(); },
    });
    return [
      nav("Home", "/home", "Voice & chat interface"),
      nav("Inbox", "/inbox", "Messages and notifications"),
      nav("Runs", "/runs", "Agent run history"),
      nav("Agents", "/agents", "Manage agents"),
      nav("Tasks", "/tasks", "Sub-agent army"),
      nav("Automations", "/automations", "Scheduled cron workflows"),
      nav("Memory", "/memory", "Remembered facts"),
      nav("Files", "/files", "Artifacts and uploads"),
      nav("MCP Hub", "/mcp-hub", "Model context protocols"),
      nav("Settings", "/settings", "Models, voice, profile"),
      ...(auth.isAdmin ? [nav("Observability", "/observability", "Costs, SLOs and audit")] : []),
      {
        id: "act-theme",
        label: "Toggle theme",
        hint: "Switch between light and dark",
        shortcut: "T",
        category: "action",
        action: () => { cycle(); onClose(); },
      },
      {
        id: "act-signout",
        label: "Sign out",
        hint: "End this session",
        category: "action",
        action: () => { void auth.signOut(); onClose(); },
      },
    ];
  }, [navigate, auth, cycle, onClose]);
}

type ToolRunStatus = { toolId: string; state: "running" | "ok" | "error"; message?: string };

/** A 409 the user hasn't answered yet. */
type PendingConfirm = { toolId: string; detail: ConfirmationRequiredDetail };

function errorMessage(err: unknown): string {
  return apiErrorMessage(err);
}

/**
 * Inline consent prompt for a 409 (PROD-2c). Resolves by re-invoking
 * the same call with the 409's confirmation token.
 */
function ConfirmationCard({
  detail,
  busy,
  onApprove,
  onCancel,
  onOpenToolPage,
}: {
  detail: ConfirmationRequiredDetail;
  busy: boolean;
  onApprove: () => void;
  onCancel: () => void;
  onOpenToolPage: () => void;
}) {
  const binary = isBinaryConfirmation(detail);
  const cost = detail.metadata?.estimated_cost_usd as number | string | undefined;

  return (
    <div
      role="alertdialog"
      aria-label="Confirmation required"
      className="border-t border-warning bg-warning/5 px-4 py-3 space-y-3"
    >
      <div>
        <p className="text-[10px] uppercase tracking-wider text-warning">
          Confirm — {detail.reason.replace(/_/g, " ")}
        </p>
        <p className="mt-1 text-sm text-fg">{detail.description}</p>
        {cost !== undefined && (
          <p className="mt-1 text-xs text-fg-muted">
            Estimated cost: <span className="font-mono">${cost}</span>
          </p>
        )}
      </div>

      {binary ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onApprove}
            disabled={busy}
            className="bg-accent px-3 py-1.5 text-xs text-accent-fg transition-opacity disabled:opacity-40 hover:opacity-90"
          >
            {busy ? "Running…" : "Approve & run"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="border border-border px-3 py-1.5 text-xs text-fg transition-colors disabled:opacity-40 hover:bg-bg-sunken"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-fg-muted">
            This tool needs more than a yes — the palette can't collect it
            here.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onOpenToolPage}
              className="bg-accent px-3 py-1.5 text-xs text-accent-fg transition-opacity hover:opacity-90"
            >
              Open tool page
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="border border-border px-3 py-1.5 text-xs text-fg transition-colors hover:bg-bg-sunken"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function CommandPalette({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [query, setQuery] = React.useState("");
  const [activeIdx, setActiveIdx] = React.useState(0);
  const [runStatus, setRunStatus] = React.useState<ToolRunStatus | null>(null);
  const [pendingConfirm, setPendingConfirm] = React.useState<PendingConfirm | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const client = useApiClient();

  const runTool = React.useCallback(
    async (toolId: string, { confirmationToken }: { confirmationToken?: string } = {}) => {
      setRunStatus({ toolId, state: "running" });
      // Non-throwing so the 409 can be read off `response.status` — a
      // confirmation prompt is a normal outcome here, not a failure.
      const { error, response } = await invokeTool({
        client,
        path: { tool_id: toolId },
        body: { arguments: {} },
        ...(confirmationToken
          ? { headers: { "x-omni-confirmation": confirmationToken } }
          : {}),
      });

      if (response.status === 409) {
        const detail = (error as { detail?: ConfirmationRequiredDetail } | undefined)
          ?.detail;
        if (detail) {
          setPendingConfirm({ toolId, detail });
          setRunStatus(null);
          return;
        }
      }
      if (error) {
        setPendingConfirm(null);
        setRunStatus({ toolId, state: "error", message: errorMessage(error) });
        return;
      }

      setPendingConfirm(null);
      setRunStatus({ toolId, state: "ok" });
      setTimeout(() => {
        setRunStatus((s) => (s?.toolId === toolId ? null : s));
        onClose();
      }, 900);
    },
    [client, onClose],
  );

  const staticItems = usePaletteItems(onClose);
  const toolItems = useToolItems(isOpen, navigate, onClose, runTool);
  const items = React.useMemo(
    () => [...staticItems, ...toolItems],
    [staticItems, toolItems],
  );

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.label.toLowerCase().includes(q) ||
        (it.hint ?? "").toLowerCase().includes(q),
    );
  }, [items, query]);

  // Reset when opened / query changes
  React.useEffect(() => {
    setActiveIdx(0);
  }, [query, isOpen]);

  // Focus input when opened
  React.useEffect(() => {
    if (isOpen) {
      setQuery("");
      setRunStatus(null);
      setPendingConfirm(null);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        // Escape backs out of a pending confirmation first, so a
        // mis-triggered guarded tool doesn't force closing the palette.
        if (pendingConfirm) { setPendingConfirm(null); return; }
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        // While a confirmation is up, the list is inert — the user
        // must answer it explicitly rather than Enter-ing past it.
        if (pendingConfirm) return;
        filtered[activeIdx]?.action();
      }
    },
    [filtered, activeIdx, onClose, pendingConfirm],
  );

  // Scroll active item into view
  React.useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  if (!isOpen) return null;

  return (
    <div
      className="om-fade-in fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4"
      style={{ background: "var(--om-color-overlay)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-xl bg-bg-elevated border border-border shadow-xl"
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-label="Command palette"
        aria-modal="true"
      >
        {/* Search input */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-fg-faint flex-shrink-0"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search commands and pages…"
            className="flex-1 bg-transparent text-sm text-fg placeholder:text-fg-faint focus:outline-none"
            aria-autocomplete="list"
            aria-expanded="true"
          />
          <kbd className="text-[10px] font-mono text-fg-faint border border-border px-1.5 py-0.5">
            esc
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[360px] overflow-y-auto py-1" role="listbox">
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-fg-faint">
              No commands match "{query}"
            </p>
          ) : (
            <>
              {["navigate", "action", "tool"].map((cat) => {
                const catItems = filtered.filter((it) => it.category === cat);
                if (catItems.length === 0) return null;
                return (
                  <div key={cat}>
                    <div className="px-4 py-1.5 text-[10px] font-mono uppercase tracking-wider text-fg-faint">
                      {cat === "navigate" ? "Go to" : cat === "action" ? "Actions" : "Tools"}
                    </div>
                    {catItems.map((item) => {
                      const idx = filtered.indexOf(item);
                      const status = runStatus?.toolId && item.id === `tool-${runStatus.toolId}` ? runStatus : null;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          data-idx={idx}
                          role="option"
                          aria-selected={idx === activeIdx}
                          disabled={status?.state === "running"}
                          onClick={item.action}
                          onMouseEnter={() => setActiveIdx(idx)}
                          className={cn(
                            "flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors disabled:opacity-60",
                            idx === activeIdx
                              ? "bg-accent-bg text-fg"
                              : "text-fg hover:bg-bg-sunken",
                          )}
                        >
                          <span className="flex-1">{item.label}</span>
                          {status?.state === "running" && (
                            <span className="text-xs text-fg-faint">running…</span>
                          )}
                          {status?.state === "ok" && (
                            <span className="text-xs text-green-500">done</span>
                          )}
                          {status?.state === "error" && (
                            <span className="text-xs text-red-500 truncate max-w-[180px]">
                              {status.message ?? "failed"}
                            </span>
                          )}
                          {!status && item.hint && (
                            <span className="text-xs text-fg-faint truncate max-w-[180px]">
                              {item.hint}
                            </span>
                          )}
                          {item.shortcut && (
                            <kbd className="text-[10px] font-mono text-fg-faint border border-border px-1">
                              {item.shortcut}
                            </kbd>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* HITL consent prompt for a 409 (PROD-2c) */}
        {pendingConfirm && (
          <ConfirmationCard
            detail={pendingConfirm.detail}
            busy={runStatus?.state === "running"}
            onApprove={() =>
              void runTool(pendingConfirm.toolId, {
                confirmationToken: pendingConfirm.detail.confirmation_token,
              })
            }
            onCancel={() => setPendingConfirm(null)}
            onOpenToolPage={() => {
              navigate(`/tools/${encodeURIComponent(pendingConfirm.toolId)}`);
              onClose();
            }}
          />
        )}

        {/* Footer hint */}
        <div className="border-t border-border px-4 py-2 flex items-center gap-3 text-[10px] text-fg-faint font-mono">
          <span>↑↓ navigate</span>
          <span>↵ select</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
